/**
 * SelectorDeCuenta.tsx — elegir una cuenta buscando, sin poder teclear un código.
 *
 * ▶▶ POR QUÉ NO ES UN `<input>` NI UN `<datalist>`
 *
 * La cuenta habitual de un proveedor se escribía a mano en un campo de texto.
 * Un `6210` tecleado como `6120` entra sin quejarse —los dos existen— y sale a
 * la luz en el estado de resultados de fin de mes, con las facturas de ese
 * proveedor ya emitidas contra la cuenta equivocada. La columna existe para
 * ahorrar trabajo, y escrita a mano lo que ahorra es el aviso.
 *
 * Un `<datalist>` tampoco: sugiere, pero acepta cualquier cosa que se teclee.
 * Una ayuda que no impide el error sólo lo hace menos probable, y acá el error
 * no se descubre el día que se comete.
 *
 * ▶ Así que se teclea para BUSCAR y se elige de la lista. Lo que sale del
 * componente es siempre un código que existe, o la cadena vacía.
 *
 * ── Se busca por código Y por los DOS nombres ─────────────────────────────
 *
 * Igual que el filtro del catálogo: quien aprendió el catálogo en inglés busca
 * «payroll» y tiene que encontrar «Salarios». El catálogo es bilingüe y la
 * búsqueda de una de las dos mitades es media búsqueda.
 *
 * ── Y se agrupa por categoría ─────────────────────────────────────────────
 *
 * Con veintinueve cuentas de gasto, una lista plana obliga a leerlas todas para
 * saber si falta alguna. El primer dígito ES la categoría NIIF 18, así que la
 * agrupación no es decorativa: es el mismo criterio con el que se arma el
 * estado de resultados.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { nombreDe } from '../nucleo/catalogo.tsx';
import type { CuentaReal } from '../datos/libros.ts';

interface Props {
  /** Las cuentas entre las que se puede elegir. Ya filtradas por quien llama. */
  readonly cuentas: readonly CuentaReal[];
  readonly valor: string;
  readonly alCambiar: (codigo: string) => void;
  /** Lo que se lee cuando no hay nada elegido. */
  readonly vacio: string;
  readonly deshabilitado?: boolean;
}

/** El rótulo del grupo. El primer dígito es la categoría, no una convención. */
const GRUPO: Record<string, string> = {
  1: 'Activo', 2: 'Pasivo', 3: 'Patrimonio', 4: 'Ingresos', 5: 'Costos',
  6: 'Gastos', 7: 'Otros resultados', 8: 'Financiación', 9: 'Impuesto de renta',
};

function normal(s: string): string {
  // Sin tildes y en minúscula: quien escribe «papeleria» tiene que encontrar
  // «Papelería». Teclear la tilde no puede ser parte de la búsqueda.
  return s.normalize('NFD').replaceAll(/[̀-ͯ]/g, '').toLowerCase();
}

export function SelectorDeCuenta({
  cuentas, valor, alCambiar, vacio, deshabilitado = false,
}: Props) {
  const { idioma } = useIdioma();
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [marcada, setMarcada] = useState(0);
  const caja = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const idLista = useId();

  const elegida = cuentas.find((c) => c.codigo === valor);

  const encontradas = useMemo(() => {
    const q = normal(texto.trim());
    if (q === '') return cuentas;
    return cuentas.filter((c) => normal(
      `${c.codigo} ${c.nombre} ${c.nombreEn ?? ''}`).includes(q));
  }, [cuentas, texto]);

  /* ▶ La marca se recorta al tamaño de la lista cuando la lista se encoge.
     Sin esto, escribir una letra más deja la marca en un índice que ya no
     existe y `Enter` no elige nada — sin decir por qué. */
  useEffect(() => { setMarcada(0); }, [texto]);

  // Cerrar al hacer clic afuera. Un panel que se queda abierto tapa el campo
  // siguiente y parece que el formulario se trabó.
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  useEffect(() => { if (abierto) campo.current?.focus(); }, [abierto]);

  const elegir = (codigo: string) => {
    alCambiar(codigo);
    setAbierto(false);
    setTexto('');
  };

  const teclas = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMarcada((i) => Math.min(i + 1, encontradas.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMarcada((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = encontradas[marcada];
      if (c) elegir(c.codigo);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setAbierto(false);
    }
  };

  // Los grupos, en el orden del catálogo. `Map` y no un objeto: conserva el
  // orden de inserción, que es el orden de los códigos.
  const porGrupo = new Map<string, CuentaReal[]>();
  for (const c of encontradas) {
    const g = GRUPO[c.codigo[0] ?? ''] ?? '—';
    const lista = porGrupo.get(g);
    if (lista) lista.push(c);
    else porGrupo.set(g, [c]);
  }

  let indice = -1;

  return (
    <div className="combo" ref={caja}>
      <button
        type="button" className="combo__boton" disabled={deshabilitado}
        aria-haspopup="listbox" aria-expanded={abierto}
        onClick={() => setAbierto((x) => !x)}
      >
        {elegida
          ? (
            <span className="combo__elegida">
              <span className="combo__cod">{elegida.codigo}</span>
              {/* ▶ El nombre va en su PROPIO span. Medido a 375px con
                  «Licencias de terceros incluidas en mantenimiento»: el texto
                  ocupa 345px en una caja de 169 y se cortaba a mitad de
                  palabra, sin puntos suspensivos. El `text-overflow` estaba en
                  el contenedor —que es `flex`— y ahí no aplica: recorta el
                  elemento que tiene el texto, no a sus hijos. */}
              <span className="combo__nom">{nombreDe(elegida, idioma)}</span>
            </span>
          )
          : <span className="combo__vacio">{vacio}</span>}
        <span className="combo__flecha" aria-hidden="true">▾</span>
      </button>

      {/* ▶ Y una salida. Sin esto, una cuenta habitual puesta por error no se
          puede quitar: el desplegable ofrece cuentas y ninguna de ellas es
          «ninguna». Vaciar un campo tiene que costar lo mismo que llenarlo. */}
      {elegida && !deshabilitado && (
        <button
          type="button" className="combo__quitar"
          onClick={() => elegir('')}
        >
          {idioma === 'en' ? 'clear' : 'quitar'}
        </button>
      )}

      {abierto && (
        <div className="combo__panel">
          <input
            ref={campo} className="input combo__buscar" value={texto}
            placeholder={idioma === 'en' ? 'Search code or name…' : 'Buscar código o nombre…'}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={teclas}
            role="combobox" aria-expanded aria-controls={idLista} aria-autocomplete="list"
          />
          <div className="combo__lista" role="listbox" id={idLista}>
            {encontradas.length === 0 && (
              <p className="combo__nada">
                {idioma === 'en' ? 'No account matches.' : 'Ninguna cuenta coincide.'}
              </p>
            )}
            {[...porGrupo].map(([grupo, lista]) => (
              <div key={grupo}>
                <p className="combo__grupo">{grupo}</p>
                {lista.map((c) => {
                  indice += 1;
                  const i = indice;
                  return (
                    <button
                      key={c.codigo} type="button" role="option"
                      aria-selected={c.codigo === valor}
                      className={`combo__op${i === marcada ? ' combo__op--marcada' : ''}`}
                      onMouseEnter={() => setMarcada(i)}
                      onClick={() => elegir(c.codigo)}
                    >
                      <span className="combo__cod">{c.codigo}</span>
                      {nombreDe(c, idioma)}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
